import { useSWRConfig } from 'swr';

import { useShop } from './useShop';

// Deliberate client-side mirror of the server formula for the optimistic bump ONLY.
// The single source of truth is compute-score.js (`score = min(100, round(baseline +
// earned * pct))`); the revalidation that follows each mutate settles any divergence
// within a few hundred ms. If the backend formula changes shape (floor stops being
// additive, cap moves, …), update this in lockstep.
const scoreFrom = (baseline, earned, pct) => Math.min(100, Math.round(baseline + earned * pct));

/**
 * Pure optimistic mutators of the `['local-potential', shopId]` cache value, so
 * the gauge and cards move the instant the retailer clicks rather than after the
 * ~400ms revalidation. The resolver returns per-item uplifts in `groups[]`, so we
 * can predict the new score locally; the server revalidation that follows settles
 * any per-bucket cap overshoot.
 */
export function activateItemOptimistically(current, id) {
	if (!current?.groups) return current;
	const item = current.groups.flatMap((g) => g.items).find((i) => i.id === id);
	if (!item || item.activated) return current;
	const groups = current.groups.map((g) => ({ ...g, items: g.items.map((i) => (i.id === id ? { ...i, activated: true } : i)) }));
	const earned = Math.min(current.earnedMax, current.earned + (item.uplift || 0));
	return {
		...current,
		groups,
		earned,
		score: scoreFrom(current.baseline, earned, current.productVisible.pct),
		suggestions: (current.suggestions || []).filter((s) => s.id !== id),
		quickWins: (current.quickWins || []).filter((q) => q.id !== id)
	};
}

export function deactivateItemOptimistically(current, id) {
	if (!current?.groups) return current;
	const item = current.groups.flatMap((g) => g.items).find((i) => i.id === id);
	if (!item || !item.activated) return current;
	const groups = current.groups.map((g) => ({ ...g, items: g.items.map((i) => (i.id === id ? { ...i, activated: false } : i)) }));
	const earned = Math.max(0, current.earned - (item.uplift || 0));
	return { ...current, groups, earned, score: scoreFrom(current.baseline, earned, current.productVisible.pct) };
}

export function dismissHintOptimistically(current, namespacedId) {
	if (!current) return current;
	const [ns, id] = namespacedId.split(':');
	if (ns === 'suggestion') return { ...current, suggestions: (current.suggestions || []).filter((s) => s.id !== id) };
	if (ns === 'quickwin') return { ...current, quickWins: (current.quickWins || []).filter((q) => q.id !== id) };
	return current;
}

/**
 * Returns an `invalidateScore()` for the active shop. Wire it into every write
 * that changes a score input (channel toggle, plan change, ad budget, profile
 * edit) — the same explicit-invalidation pattern `useChannelActions` already uses
 * for `['billing-status', shopId]`.
 */
export function useInvalidateLocalPotential() {
	const { mutate } = useSWRConfig();
	const { shop } = useShop();
	return () => mutate(['local-potential', shop?.id]);
}
